import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faCalculator } from '@fortawesome/free-solid-svg-icons';
import { calculateDateDifference } from '../utils/dateUtils';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import tr from 'date-fns/locale/tr';

// Türkçe lokalizasyonunu kaydet
registerLocale('tr', tr);
setDefaultLocale('tr');

// Ekran boyutunu kontrol etmek için bir hook ekleyelim
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const media = window.matchMedia(query);
    const listener = (e) => setMatches(e.matches);
    setMatches(media.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [query]);

  return matches;
};

// DD/MM/YYYY -> YYYY-MM-DD (timezone-güvenli, Date objesi yok)
const toISO = (ddmmyyyy) => {
  if (!ddmmyyyy || ddmmyyyy.length !== 10) return '';
  const [d, m, y] = ddmmyyyy.split('/');
  if (!d || !m || !y) return '';
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
};

// YYYY-MM-DD -> DD/MM/YYYY (timezone-güvenli, Date objesi yok)
const fromISO = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return '';
  return `${d}/${m}/${y}`;
};

const pad = (n) => String(n).padStart(2, '0');

const DateInput = React.memo(({ label, value, onChange, onBlur, onKeyPress, onComplete = () => {}, inputRef, isEndDate, autoFocusRef, error }) => {
  const [localValue, setLocalValue] = useState(value);
  const isAutoFocused = useRef(false);
  const buttonRef = useRef(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const isLargeScreen = useMediaQuery('(min-width: 768px)');

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const formatDate = useCallback((input) => {
    const cleaned = input.replace(/[^\d]/g, '');
    const match = cleaned.match(/^(\d{1,2})(\d{1,2})?(\d{0,4})?$/);
    if (match) {
      const [, day, month, year] = match;
      if (day) {
        if (month) {
          if (year) {
            return `${day}/${month}/${year}`;
          }
          return `${day}/${month}`;
        }
        return day;
      }
    }
    return cleaned;
  }, []);

  const handleChange = useCallback((e) => {
    let input = e.target.value;
    if (input.replace(/[^\d]/g, '').length <= 8) {
      const formatted = formatDate(input);
      setLocalValue(formatted);
      onChange(formatted);
      
      if (formatted.length === 10) {
        onComplete();
      }
    }
    isAutoFocused.current = false;
  }, [formatDate, onChange, onComplete]);

  const handleBlur = useCallback((e) => {
    let formatted = e.target.value;
    if (formatted.length === 8 && !formatted.includes('/')) {
      formatted = `${formatted.slice(0, 2)}/${formatted.slice(2, 4)}/${formatted.slice(4)}`;
    } else if (formatted.length > 5 && formatted.indexOf('/') === 2 && formatted.lastIndexOf('/') === 2) {
      formatted = `${formatted.slice(0, 5)}/${formatted.slice(5)}`;
    }
    setLocalValue(formatted);
    onChange(formatted);
    onBlur(e);
  }, [onChange, onBlur]);

  const handleFocus = useCallback(() => {
    if (isEndDate && autoFocusRef.current) {
      // Otomatik odaklanma durumunda içeriği silme
      isAutoFocused.current = true;
      autoFocusRef.current = false;
    } else {
      // Eğer otomatik odaklanma değilse içeriği sil
      setLocalValue('');
      onChange('');
    }
  }, [onChange, isEndDate, autoFocusRef]);

  const handleCalendarClick = useCallback((e) => {
    e.stopPropagation();
    setIsDatePickerOpen(true);
  }, []);

  const handleNativeDateChange = useCallback((e) => {
    const formatted = fromISO(e.target.value);
    if (formatted) {
      setLocalValue(formatted);
      onChange(formatted);
      // Mobilde otomatik geçiş yok: onComplete() çağrılmaz (klavye sıçramasın).
      // İki tarih de geçerliyse otomatik hesaplama parent'ın handleDateChange'inde yapılır.
    }
  }, [onChange]);

  const handleButtonMouseDown = useCallback((e) => {
    // Fare olaylarını engelle
    e.stopPropagation();
  }, []);

  const handleDatePickerChange = useCallback((date) => {
    if (date) {
      const formattedDate = date.toLocaleDateString('tr-TR', {day: '2-digit', month: '2-digit', year: 'numeric'}).replace(/\./g, '/');
      setLocalValue(formattedDate);
      onChange(formattedDate);
      onComplete();
    }
    setIsDatePickerOpen(false);
  }, [onChange, onComplete]);

  // parseDate fonksiyonunu ekleyelim
  const parseDate = useCallback((dateString) => {
    if (dateString.length !== 10) return null;
    const [day, month, year] = dateString.split(/[-./]/);
    return new Date(year, month - 1, day);
  }, []);

  const currentYear = new Date().getFullYear();
  const yearRange = 100; // 100 yıllık bir aralık belirledik, isteğe göre değiştirilebilir
  const now = new Date();
  const todayISO = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const minISO = `${currentYear - yearRange}-01-01`;

  return (
    <div className="relative">
      <label className="app-label">
        {label}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyPress={onKeyPress}
          className="app-input app-num pr-12"
          placeholder="GG/AA/YYYY"
        />
        {!isLargeScreen && (
          <>
            <span className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 flex items-center justify-center text-amber-600">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-xl" />
            </span>
            <input
              type="date"
              value={toISO(localValue)}
              max={todayISO}
              min={minISO}
              onChange={handleNativeDateChange}
              aria-label="Takvimden tarih seç"
              tabIndex={-1}
              className="absolute right-0 top-0 bottom-0 w-12 z-10 opacity-0 appearance-none cursor-pointer"
            />
          </>
        )}
        {isLargeScreen && (
          <button
            ref={buttonRef}
            onClick={handleCalendarClick}
            onMouseDown={handleButtonMouseDown}
            onTouchStart={handleButtonMouseDown}
            className="absolute right-0 top-0 bottom-0 px-3 flex items-center justify-center text-amber-600 hover:bg-amber-50 rounded-r-xl transition-colors duration-200"
          >
            <FontAwesomeIcon icon={faCalendarAlt} className="text-xl" />
          </button>
        )}
        {isLargeScreen && isDatePickerOpen && (
          <DatePicker
            selected={localValue ? parseDate(localValue) : null}
            onChange={handleDatePickerChange}
            onClickOutside={() => setIsDatePickerOpen(false)}
            inline
            dateFormat="dd/MM/yyyy"
            locale="tr"
            showYearDropdown
            showMonthDropdown
            scrollableYearDropdown
            scrollableMonthYearDropdown
            yearDropdownItemNumber={yearRange}
            minDate={new Date(currentYear - yearRange, 0, 1)}
            maxDate={new Date()}
            monthNames={[
              "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
              "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
            ]}
            monthNamesShort={[
              "Oca", "Şub", "Mar", "Nis", "May", "Haz",
              "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"
            ]}
            dayNames={["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"]}
            dayNamesShort={["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"]}
            dayNamesMin={["Pz", "Pt", "Sa", "Ça", "Pe", "Cu", "Ct"]}
            dropdownMode="select"
          />
        )}
      </div>
      {error && (
        <div className="absolute top-0 right-0 mt-2 mr-2">
          <span className="bg-rose-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
            {error}
          </span>
        </div>
      )}
    </div>
  );
});

const DateDifference = () => {
  const [dates, setDates] = useState({ start: '', end: '' });
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({ start: '', end: '' });
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const autoFocusRef = useRef(false);

  const parseDate = useCallback((dateString) => {
    if (dateString.length !== 10) return null;
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day);
  }, []);

  const isValidDate = useCallback((dateString) => {
    if (dateString.length !== 10) return false;
    const [day, month, year] = dateString.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    return date && date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  }, []);

  const calculateDifference = useCallback((currentDates = dates) => {
    const { start, end } = currentDates;
    
    // Tarih alanlarının dolu olup olmadığını kontrol et
    if (!start || !end || start.length !== 10 || end.length !== 10) {
      setResult(null);
      setErrors({
        start: start.length !== 10 ? 'Geçersiz tarih' : '',
        end: end.length !== 10 ? 'Geçersiz tarih' : ''
      });
      return;
    }

    if (isValidDate(start) && isValidDate(end)) {
      const startDate = parseDate(start);
      const endDate = parseDate(end);
      const [earlierDate, laterDate] = startDate <= endDate ? [startDate, endDate] : [endDate, startDate];
      setResult(calculateDateDifference(earlierDate, laterDate));
      setErrors({ start: '', end: '' });
    } else {
      setResult(null);
      setErrors({
        start: !isValidDate(start) ? 'Geçersiz tarih' : '',
        end: !isValidDate(end) ? 'Geçersiz tarih' : ''
      });
    }
  }, [parseDate, isValidDate, dates]);

  const handleDateChange = useCallback((field) => (value) => {
    setDates(prev => {
      const newDates = { ...prev, [field]: value };
      if (value.length === 10) {
        if (!isValidDate(value)) {
          setErrors(prev => ({ ...prev, [field]: 'Geçersiz tarih' }));
        } else {
          setErrors(prev => ({ ...prev, [field]: '' }));
          const otherField = field === 'start' ? 'end' : 'start';
          if (newDates[otherField].length === 10 && isValidDate(newDates[otherField])) {
            calculateDifference(newDates);
          }
        }
      } else {
        setErrors(prev => ({ ...prev, [field]: '' }));
        setResult(null);
      }
      return newDates;
    });
  }, [isValidDate, calculateDifference]);

  const handleDateBlur = useCallback((field) => (e) => {
    const formattedDate = e.target.value.replace(/\D/g, '').replace(/(\d{2})(\d{2})(\d{4})/, '$1/$2/$3');
    setDates(prev => ({ ...prev, [field]: formattedDate }));
  }, []);

  const handleStartDateComplete = useCallback(() => {
    if (endDateRef.current) {
      autoFocusRef.current = true;
      endDateRef.current.focus();
    }
  }, []);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      if (dates.start && dates.end) {
        calculateDifference();
      } else if (dates.start && !dates.end && endDateRef.current) {
        endDateRef.current.focus();
      }
    }
  }, [dates, calculateDifference]);

  const dateInputs = useMemo(() => [
    { label: "Başlangıç Tarihi", value: dates.start, onChange: handleDateChange('start'), ref: startDateRef, onComplete: handleStartDateComplete, error: errors.start },
    { label: "Bitiş Tarihi", value: dates.end, onChange: handleDateChange('end'), ref: endDateRef, isEndDate: true, error: errors.end }
  ], [dates, handleDateChange, handleStartDateComplete, errors]);

  return (
    <div className="mx-auto max-w-3xl">
      <div className="app-card mb-8 animate-fade-in-up">
        <div className="app-header">
          <div className="relative z-10 flex items-center">
            <div className="app-header-icon">
              <FontAwesomeIcon icon={faCalculator} className="w-4 h-4" />
            </div>
            <h1 className="app-header-title ml-3">
              Tarih ve Yaş Hesaplama
            </h1>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            {dateInputs.map((input, index) => (
              <DateInput
                key={index}
                label={input.label}
                value={input.value}
                onChange={input.onChange}
                onBlur={handleDateBlur(index === 0 ? 'start' : 'end')}
                onKeyPress={handleKeyPress}
                onComplete={input.onComplete}
                inputRef={input.ref}
                isEndDate={input.isEndDate}
                autoFocusRef={autoFocusRef}
                error={input.error}
              />
            ))}
          </div>

          <button
            onClick={() => calculateDifference()}
            className="btn-primary w-full"
          >
            <FontAwesomeIcon icon={faCalculator} className="text-amber-300" />
            Hesapla
          </button>

          {result && dates.start.length === 10 && dates.end.length === 10 && (
            <div className="app-panel-dark mt-6 animate-fade-in-up">
              <div className="relative z-10 text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-300/80">
                  Hesaplama Sonucu
                </p>
                <div className="mt-2 flex items-center justify-center gap-2 text-sm text-slate-300">
                  <span className="app-num">{dates.start}</span>
                  <span className="text-amber-300/70">→</span>
                  <span className="app-num">{dates.end}</span>
                </div>
                <p className="mt-4 font-display text-3xl font-semibold text-white sm:text-4xl [text-shadow:0_1px_12px_rgba(0,0,0,0.25)]">
                  {result}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DateDifference;
